import { useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { BarChart3, Battery, Check, ChevronDown, Grid3X3, Layers, Zap } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx';
import { cn } from '@/utils/utils.ts';

const SETUP_TYPES = {
  ALL: 'all',
  PRODUCTION_ONLY: 'production-only',
  PRODUCTION_STORAGE: 'production-storage',
  STORAGE_ONLY: 'storage-only',
} as const;

export const SetupTypeLiteral = [
  'all',
  'production-only',
  'production-storage',
  'storage-only',
] as const;

export const ViewTypeLiteral = ['production', 'storage', 'overview'] as const;

export type SetupType = (typeof SETUP_TYPES)[keyof typeof SETUP_TYPES];
export type ViewType = 'production' | 'storage' | 'overview';

const viewOptions = [
  {
    value: 'overview' as ViewType,
    label: 'Overview',
    icon: BarChart3,
    description: 'Display plant data based on plant type',
  },
  {
    value: 'production' as ViewType,
    label: 'Production View',
    icon: Zap,
    description: 'Focus on production metrics and data',
  },
  {
    value: 'storage' as ViewType,
    label: 'Storage View',
    icon: Battery,
    description: 'Focus on storage and battery data',
  },
];

const setupTypeOptions = [
  {
    value: SETUP_TYPES.ALL,
    label: 'All Plants',
    icon: Grid3X3,
    description: 'Display relevant plant types for the selected view',
  },
  {
    value: SETUP_TYPES.PRODUCTION_STORAGE,
    label: 'Production and Storage',
    icon: Layers,
    description: 'Plants with both production and storage',
  },
  {
    value: SETUP_TYPES.PRODUCTION_ONLY,
    label: 'Production Only',
    icon: Zap,
    description: 'Plants with production capabilities only',
  },

  {
    value: SETUP_TYPES.STORAGE_ONLY,
    label: 'Storage Only',
    icon: Battery,
    description: 'Plants with storage capabilities only',
  },
];

export type PlantsListViewFilterState = {
  view: ViewType;
  setupType: SetupType;
};

interface ViewFilterCommandProps {
  onStateChange: (state: PlantsListViewFilterState) => void;
  currentState: PlantsListViewFilterState;
}

export const PlantsListViewFilterCommand = ({
  onStateChange,
  currentState,
}: ViewFilterCommandProps) => {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [open, setOpen] = useState(false);

  const handleViewChange = (view: ViewType) => {
    let newState = { ...currentState, view };

    if (newState.view === 'storage' && newState.setupType === 'production-only') {
      newState.setupType = SETUP_TYPES.ALL;
    }

    if (newState.view === 'production' && newState.setupType === 'storage-only') {
      newState.setupType = SETUP_TYPES.ALL;
    }

    onStateChange(newState);
    setOpen(false);
  };

  const handleSetupTypeChange = (setupType: SetupType) => {
    let newState = { ...currentState, setupType };

    if (newState.view === 'storage' && newState.setupType === 'production-only') {
      newState.view = 'overview';
    }

    if (newState.view === 'production' && newState.setupType === 'storage-only') {
      newState.view = 'overview';
    }

    onStateChange(newState);
    setOpen(false);
  };

  const getButtonLabel = () => {
    const viewLabel =
      viewOptions.find((v) => v.value === currentState.view)?.label || 'Overview';
    const setupLabel =
      setupTypeOptions.find((s) => s.value === currentState.setupType)?.label || 'All Plants';

    if (currentState.setupType === SETUP_TYPES.ALL) {
      return `${viewLabel} • All Plants`;
    }

    return `${viewLabel} • ${setupLabel}`;
  };

  const getButtonIcon = () => {
    const viewOption = viewOptions.find((v) => v.value === currentState.view);

    if (!viewOption) return <BarChart3 className="h-4 w-4 text-gray-600" />;

    const Icon = viewOption.icon;
    return <Icon className="h-4 w-4 text-gray-600" />;
  };

  return (
    <div className="flex">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-auto min-w-[215px] justify-between rounded-md border border-gray-200 bg-white/80 px-4 py-2.5 transition-all duration-200 hover:bg-white/90 hover:shadow-md"
          >
            <div className="flex items-center gap-2">
              {getButtonIcon()}
              <span className="text-sm font-medium text-gray-700">{getButtonLabel()}</span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 rounded-xl border border-gray-200/50 bg-white/95 p-0 shadow-xl backdrop-blur-md"
          align="start"
          onOpenAutoFocus={(e) => isMobile && e.preventDefault()}
        >
          <Command className="rounded-xl">
            <CommandInput
              placeholder="Search views and filters..."
              className="border-none text-sm focus:ring-0"
            />
            <CommandList>
              <CommandEmpty>No options found.</CommandEmpty>

              <CommandGroup heading="View Type">
                {viewOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = currentState.view === option.value;
                  const isOverview = option.value === 'overview';

                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleViewChange(option.value)}
                      className={cn(
                        'mx-1 flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50',
                        isOverview && 'mb-2 border border-blue-200 bg-blue-50/50'
                      )}
                    >
                      <Icon
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isOverview ? 'text-blue-600' : 'text-gray-500'
                        )}
                      />
                      <div className="flex-1">
                        <div
                          className={cn('text-sm font-medium', isOverview && 'text-blue-900')}
                        >
                          {option.label}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {option.description}
                        </div>
                      </div>
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isSelected ? 'text-blue-600 opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>

              <CommandSeparator className="my-2" />

              <CommandGroup heading="Plant Filter">
                {setupTypeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = currentState.setupType === option.value;
                  const isAllOption = option.value === SETUP_TYPES.ALL;

                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSetupTypeChange(option.value)}
                      className={cn(
                        'mx-1 flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50',
                        isAllOption && 'mb-2 border border-gray-200 bg-gray-50/50'
                      )}
                    >
                      <Icon
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isAllOption ? 'text-gray-700' : 'text-gray-500'
                        )}
                      />
                      <div className="flex-1">
                        <div
                          className={cn('text-sm font-medium', isAllOption && 'text-gray-900')}
                        >
                          {option.label}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {option.description}
                        </div>
                      </div>
                      <Check
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          isSelected ? 'text-blue-600 opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
